import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Self-service "Change my login email" card.
 * Calls PUT /api/me/email. Requires current password so a stolen session
 * can't quietly transfer the account.
 */
export default function ChangeEmailCard({ compact = false, testId = "change-email" }) {
  const { user, refresh } = useAuth();
  const [next, setNext] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = next.trim().toLowerCase();
    if (!v || !v.includes("@")) return toast.error("Enter a valid email");
    if (v === (user?.email || "").toLowerCase()) return toast.error("That's already your email");
    if (!pwd) return toast.error("Confirm your current password");
    setBusy(true);
    try {
      await api.put("/me/email", { new_email: v, password: pwd });
      // Refresh the in-memory user so the header label / future requests use the new email.
      if (refresh) await refresh();
      toast.success("Login email updated");
      setNext(""); setPwd("");
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className={`border border-border ${compact ? "p-5" : "p-8"} space-y-4`} data-testid={`${testId}-card`}>
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-primary" />
        <div>
          <h2 className={`font-display ${compact ? "text-lg" : "text-xl"} font-bold tracking-tight`}>Change my login email</h2>
          <p className="text-xs text-muted-foreground">You&apos;ll use this new address to sign in. Confirm your current password to authorize the change.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${testId}-new`}>New email</Label>
          <Input id={`${testId}-new`} type="email" required value={next} onChange={(e) => setNext(e.target.value)}
                 className="rounded-sm h-10" autoComplete="email" data-testid={`${testId}-new`} />
        </div>
        <div>
          <Label htmlFor={`${testId}-password`}>Current password</Label>
          <PasswordInput id={`${testId}-password`} required value={pwd} onChange={(e) => setPwd(e.target.value)}
                 className="rounded-sm h-10" autoComplete="current-password" inputTestId={`${testId}-password`} testId={`${testId}-password-toggle`} />
        </div>
      </div>
      <div>
        <Button type="submit" disabled={busy} className="rounded-sm" data-testid={`${testId}-submit`}>
          {busy ? "Updating…" : "Update email"}
        </Button>
      </div>
    </form>
  );
}
